import { FormattedMessage, useIntl } from "react-intl";
import type { ResolutionChoice, RowDiff } from "./profile-merge-types";
import { MergeGuideControls } from "@/components/save/ProfileMergeGuide";
import { useGuideCard } from "@/components/save/use-guide-card";
import { useProfileMergeStore, defaultChoice, SKILLS_STEP_KEY } from "@/stores/profile-merge-store";

// In-line LinkedIn merge for the SKILLS (expertises) section. Unlike the other
// collections there is nothing to adjudicate per row — incoming skills are
// matched by name and arrive as plain ADDs — so per-row keep/use controls are
// busywork. Instead the incoming skills render like the existing skill list
// rows (name only), grouped under one neutral card that is a SINGLE step of
// the guided review (ProfileMergeGuide): its Skip/Add decision buttons decide
// all skills at once.
//
// The decision drives the same per-row resolutions in the merge store (the
// guide step spans every actionable rowKey), so the commit payload and the
// change count in the bar are unchanged — this is purely a review-UX
// simplification.
//
// Renders nothing when there is no active preview or no actionable skill rows,
// so it is safe to mount unconditionally in the section.
export function ProfileMergeSkills() {
  const intl = useIntl();
  const preview = useProfileMergeStore((s) => s.preview);
  const choices = useProfileMergeStore((s) => s.choices);
  const { activationProps } = useGuideCard(SKILLS_STEP_KEY);

  const rows = (preview?.expertises ?? []).filter((r) => r.action !== "UNCHANGED");
  if (rows.length === 0) return null;

  const choiceOf = (row: RowDiff): ResolutionChoice => choices[row.rowKey] ?? defaultChoice(row.action);
  // The single group state: importing only when EVERY skill resolves to
  // USE_INCOMING (the ADD default, so the group starts on Add). A mixed state
  // (e.g. after the bar's keep-all touched some keys) dims the group and the
  // guide's decision buttons show neither side selected; picking a side
  // normalizes all rows.
  const importing = rows.every((row) => choiceOf(row) === "USE_INCOMING");

  const skillName = (row: RowDiff): string | null => {
    const named = row.fields.find((f) => f.field === "skill");
    return named?.incomingValue && named.incomingValue.length > 0 ? named.incomingValue : null;
  };

  return (
    <div
      {...activationProps}
      className="mt-2 flex flex-col gap-2 rounded-md border border-ink/10 bg-ink/[0.02] px-3 py-2"
      data-testid="profile-merge-skills"
    >
      <div className="flex items-center gap-2">
        <span className="field-meta text-ink/55">
          <FormattedMessage id="profile.merge.incomingLabel" defaultMessage="Imported" />
        </span>
      </div>

      {/* Name-only rows, mirroring the section's existing skill list (hairline
          dividers); skipped skills dim as a group. */}
      <div className={`divide-y divide-ink/5 ${importing ? "" : "opacity-45"}`}>
        {rows.map((row) => (
          <div key={row.rowKey} className="py-2.5 first:pt-0 last:pb-0" data-testid="profile-merge-skill-row">
            <div className="text-sm font-medium leading-tight">{skillName(row) ?? "–"}</div>
            {/* A matched skill whose details differ would be REPLACED by the
                import — keep that spelled out (provenance-blind saved data). */}
            {importing && row.action === "CONFLICT" && row.existingRowId !== null ? (
              <div className="field-meta text-ink/55" data-testid="profile-merge-skill-replaces">
                <FormattedMessage id="profile.merge.replacesSavedRow" defaultMessage="Replaces your saved entry" />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <MergeGuideControls
        stepKey={SKILLS_STEP_KEY}
        action="ADD"
        ariaLabel={intl.formatMessage({
          id: "profile.merge.skills.importAllAria",
          defaultMessage: "Import all skills",
        })}
      />
    </div>
  );
}
